/**
 * @file ScopeSnapshotView.tsx
 * @description 合約評審第一節「一、確認驗證範圍」——按驗證方案（TOP / TAP）裁剪欄位（序 58）。
 *
 * 從 review.scopeSnapshot 渲染（後端建立評審時把 case + client 拍快照寫入 jsonb，
 * 形狀契約 @zhcb/shared ContractReviewScopeSnapshot）。
 *
 * ⚠️ 序 58 需求：確認驗證範圍僅保留客戶指定的欄位，其餘一律不展示——
 *
 * - **有機 TOP** 保留：農產品經營者名稱、驗證服務類型、驗證選項、驗證類別、驗證依據（固定全文）、
 *   產品品項、產品範圍、驗證場所地址共有幾處（農地 _ 筆、採收後處理場所 _ 處）、經營業者地址、
 *   驗證場所地址、採收後處理場所地址、驗證面積合計、有機證書面積 / 有機轉型期證書面積。
 * - **產銷履歷 TAP** 保留：農產品經營者名稱、驗證服務類型、驗證選項、驗證類別、驗證依據（含 OOOO
 *   良好農業規範版本，可人工修改）、產品類別、產品品項、驗證場所地址共有幾處、經營業者地址、
 *   驗證場所地址、採收後處理場所地址、驗證面積合計。
 *
 * 【驗證依據】：TOP 為法定固定全文（只讀）；TAP 的 OOOO（良好農業規範版本）預設帶入範例值，
 *   草稿階段可由前端編輯（onVerificationBasisChange），儲存時經 PATCH 合併回 scopeSnapshot。
 *   TODO：TAP 良好農業規範版本後續由後台「最新法規版本」字典自動提取。
 *
 * 場所處數 / 面積分項等由建立評審時凍結的 applicationDetails 快照帶出，缺值統一顯示「—」。
 */

import { Descriptions, Input, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  parseApplicationDetails,
  getVerificationBasisText,
  type ApplicationDetailsTap,
  type ApplicationDetailsTop,
  type ContractReviewScopeSnapshot,
} from '@zhcb/shared';

interface Props {
  /**
   * 來自 review.scopeSnapshot 的 jsonb 物件（後端寫入時為
   * ContractReviewScopeSnapshot 形狀，但 DTO 上是 Record<string, unknown>）；
   * null 時組件返回 null。
   */
  snapshot: Record<string, unknown> | null;
  /** review.createdAt，用於右下角顯示快照時間 */
  createdAt: string;
  /**
   * 驗證依據是否允許編輯（僅 TAP 草稿階段由管理組 / 組長編輯 OOOO 版本）。
   * 傳入 onVerificationBasisChange 才生效；TOP 一律只讀。
   */
  editableVerificationBasis?: boolean;
  /** 驗證依據當前受控值（編輯態下由父層維護，避免快照被就地改動） */
  verificationBasisValue?: string;
  /** 驗證依據變更回呼（編輯態） */
  onVerificationBasisChange?: (value: string) => void;
}

/** 缺值統一顯示符號 */
const DASH = '—';

/** 驗證選項 value → 中文標籤 */
const VERIFICATION_OPTION_LABELS: Record<string, string> = {
  individual: '個別驗證',
  group: '集團驗證',
};

/** 空字串 / null / undefined 統一顯示 DASH */
function text(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return DASH;
  const s = String(v).trim();
  return s ? s : DASH;
}

/** 地址快照清單（多行顯示，空清單顯示 DASH） */
function AddressList({ addresses }: { addresses?: string[] }) {
  if (!addresses?.length) return <>{DASH}</>;
  return (
    <>
      {addresses.map((addr, i) => (
        <div key={i}>{addr}</div>
      ))}
    </>
  );
}

/**
 * 場所處數（序 56：生產地筆數 / 場區筆數兩欄）：
 * - 生產地筆數：TAP 優先取明確填寫的 landCount，否則回落勾選的土地 id 數 / 地址快照行數；
 * - 場區筆數：TAP 優先取明確填寫的 siteCount（原「場區」語意），否則回落採後處理場所
 *   勾選 id 數 / 地址快照行數。
 * TOP 無明確筆數欄，一律走勾選 / 快照回落。舊資料無 landCount/siteCount 鍵時安全回落，不報錯。
 */
function siteCounts(d: ApplicationDetailsTop | ApplicationDetailsTap | undefined) {
  // landCount 僅 TAP 有；TOP 型別無此鍵，用可選鏈讀取兼容
  const explicitLand = (d as ApplicationDetailsTap | undefined)?.landCount;
  const explicitSite = (d as ApplicationDetailsTap | undefined)?.siteCount;
  const landCount =
    explicitLand ?? d?.selectedLandIds?.length ?? d?.landAddressSnapshot?.length ?? 0;
  const siteCount =
    explicitSite ?? d?.selectedSiteIds?.length ?? d?.siteAddressSnapshot?.length ?? 0;
  return { landCount, siteCount };
}

export function ScopeSnapshotView({
  snapshot,
  createdAt,
  editableVerificationBasis = false,
  verificationBasisValue,
  onVerificationBasisChange,
}: Props) {
  if (!snapshot) return null;
  // 收窄至快照形狀讀取已知字段；缺失字段顯示 DASH
  const s = snapshot as ContractReviewScopeSnapshot;

  // 申請明細（TOP / TAP 分流解析）；LEGACY / null 時降級為 undefined，欄位回落 DASH。
  const parsed = parseApplicationDetails(s.applicationDetails ?? null);
  const isTap = parsed?.kind === 'TAP';
  const topD = parsed?.kind === 'TOP' ? parsed.details : undefined;
  const tapD = parsed?.kind === 'TAP' ? parsed.details : undefined;
  const d = topD ?? tapD;

  // 驗證類別（產品類別陣列，主表 verificationCategories）
  const categories = Array.isArray(s.verificationCategories)
    ? s.verificationCategories
        .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
        .join('、')
    : DASH;

  // 驗證選項：個別 / 集團（集團另帶農戶數）
  const verificationOption = d?.verificationOption
    ? `${VERIFICATION_OPTION_LABELS[d.verificationOption] ?? d.verificationOption}${
        d.verificationOption === 'group' && d.groupFarmerCount
          ? `（農戶數 ${d.groupFarmerCount} 戶）`
          : ''
      }`
    : DASH;

  // 經營業者地址：優先戶籍地址，回落通訊地址
  const operatorAddress = text(s.registeredAddress ?? s.contactAddress);

  // 驗證依據：編輯態用受控值，只讀態讀快照（回落預設文本）
  const basisFromSnapshot = s.verificationBasis?.trim()
    ? s.verificationBasis
    : getVerificationBasisText(s.scheme);
  const basisDisplay = editableVerificationBasis
    ? (verificationBasisValue ?? basisFromSnapshot)
    : basisFromSnapshot;

  // 場所處數（農地 _ 筆、採收後處理場所 _ 處）
  const { landCount, siteCount } = siteCounts(d);

  // 產品品項 / 產品範圍 / 產品類別
  const topItems = topD?.productItems?.length
    ? topD.productItems.map((it) => it.name).join('、')
    : DASH;
  const topScopes = topD?.productScopes?.length
    ? topD.productScopes.map((it) => it.name).join('、')
    : DASH;
  // TAP 產品類別 / 品項自明細各行去重拼接
  const tapCategories = tapD?.rows?.length
    ? Array.from(new Set(tapD.rows.map((r) => r.productCategory).filter(Boolean))).join('、')
    : DASH;
  const tapItems = tapD?.rows?.length
    ? Array.from(new Set(tapD.rows.map((r) => r.productItem).filter(Boolean))).join('、')
    : DASH;

  // 驗證面積合計：TOP verificationArea / TAP totalArea
  const totalArea = isTap ? tapD?.totalArea : topD?.verificationArea;
  const totalAreaText = totalArea ? `${totalArea} 公頃` : DASH;

  // 有機證書面積 / 有機轉型期證書面積（僅 TOP，取自 LEGACY 面積欄）
  const organicAreaText = topD?.organicAreaHa !== undefined ? `${topD.organicAreaHa} 公頃` : DASH;
  const organicConversionAreaText =
    topD?.organicConversionAreaHa !== undefined ? `${topD.organicConversionAreaHa} 公頃` : DASH;

  // 驗證依據儲存格：TAP 可編輯用 TextArea，其餘只讀純文本
  const basisContent =
    editableVerificationBasis && onVerificationBasisChange ? (
      <Input.TextArea
        rows={3}
        value={basisDisplay}
        maxLength={2000}
        onChange={(e) => onVerificationBasisChange(e.target.value)}
        placeholder="請填寫驗證依據（含良好農業規範名稱與版次）"
      />
    ) : (
      <Typography.Text>{basisDisplay}</Typography.Text>
    );

  return (
    <div>
      <Descriptions title="一、確認驗證範圍" column={2} bordered size="small">
        <Descriptions.Item label="農產品經營者名稱">{text(s.clientName)}</Descriptions.Item>
        <Descriptions.Item label="驗證服務類型">{text(s.verificationType)}</Descriptions.Item>
        <Descriptions.Item label="驗證選項">{verificationOption}</Descriptions.Item>
        <Descriptions.Item label="驗證類別">{categories || DASH}</Descriptions.Item>
        <Descriptions.Item label="驗證依據" span={2}>
          {basisContent}
        </Descriptions.Item>

        {/* 產品資訊：TAP 有「產品類別」，TOP 有「產品範圍」 */}
        {isTap && <Descriptions.Item label="產品類別">{tapCategories}</Descriptions.Item>}
        <Descriptions.Item label="產品品項" span={isTap ? 1 : 2}>
          {isTap ? tapItems : topItems}
        </Descriptions.Item>
        {!isTap && (
          <Descriptions.Item label="產品範圍" span={2}>
            {topScopes}
          </Descriptions.Item>
        )}

        {/* 場所處數（序 56 拆分）：生產地 _ 筆、場區 _ 筆 */}
        <Descriptions.Item label="驗證場所地址共有幾處" span={2}>
          {`生產地 ${landCount} 筆、場區 ${siteCount} 筆`}
        </Descriptions.Item>

        {/* 三種地址 */}
        <Descriptions.Item label="經營業者地址" span={2}>
          {operatorAddress}
        </Descriptions.Item>
        <Descriptions.Item label="驗證場所地址" span={2}>
          <AddressList addresses={d?.landAddressSnapshot} />
        </Descriptions.Item>
        <Descriptions.Item label="採收後處理場所地址" span={2}>
          <AddressList addresses={d?.siteAddressSnapshot} />
        </Descriptions.Item>

        {/* 面積：合計 + TOP 有機 / 有機轉型期證書面積分項 */}
        <Descriptions.Item label="驗證面積合計" span={2}>
          {totalAreaText}
        </Descriptions.Item>
        {!isTap && (
          <>
            <Descriptions.Item label="有機證書面積">{organicAreaText}</Descriptions.Item>
            <Descriptions.Item label="有機轉型期證書面積">
              {organicConversionAreaText}
            </Descriptions.Item>
          </>
        )}
      </Descriptions>
      <div style={{ color: '#999', fontSize: 12, marginTop: 8, textAlign: 'right' }}>
        快照拍攝於 {dayjs(createdAt).format('YYYY-MM-DD HH:mm')}
      </div>
    </div>
  );
}
